import React from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { authenticationService } from '../../services';
import { InviteType } from '../../types/invites';
import { InvalidAccountResponse } from '../../types/responses';
import Button from './Button';

interface InvalidAuthBodyProps {
  info?: InvalidAccountResponse;
  invitationId: string;
  inviteType: InviteType;
}

export default function InvalidAuthBody({
  info = undefined,
  invitationId,
  inviteType,
}: InvalidAuthBodyProps): JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();

  const [emailInput, setEmailInput] = React.useState(info?.email || '');

  const handleSubmit = async () => {
    await authenticationService
      .generateMagicLink(emailInput, invitationId, '', inviteType)
      .then(() => toast.success(t('toast.success.login')))
      .catch(() => {});
  };

  const getMessage = () => {
    switch (inviteType) {
      case InviteType.EMAIL_PREFERENCE:
        return t('page.invalidAuth.message.email');
      case InviteType.INTRODUCTION:
        return t('page.invalidAuth.message.introduction');
      default:
        return t('page.invalidAuth.message.general', {
          email: info?.email,
          name: info?.alias.replace('+', ' '),
        });
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col gap-4">
      <div className="flex flex-col">
        <h2 className="mb-4 text-2xl font-semibold">
          {auth ? t('page.invalidAuth.wrongAccount') : t('page.invalidAuth.signIn')}
        </h2>
        <p className="text-sm">{getMessage()}</p>
      </div>
      {!info && (
        <div className="flex w-full flex-col">
          <label className="focus:border-3 mb-1 pr-2 text-sm focus:border-[#01AFA5] focus:ring-0 focus:ring-transparent">
            {t('form.email.email')}
          </label>
          <input
            className="h-[40px] w-full focus:ring-0 focus:ring-transparent"
            name="email"
            type="text"
            value={emailInput}
            placeholder={t('form.email.placeholder')}
            onChange={(e) => setEmailInput(e.target.value)}
          />
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <Button variant="primary" className="px-[32px]" onClick={handleSubmit}>
          {t('page.login.submit')}
        </Button>
      </div>
    </div>
  );
}
